import 'core-js/features/object/from-entries'
import '../styles/common.sass'
import '../styles/phone-input.sass'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/plugins/toolbar/prism-toolbar.css'

import { Fragment, useEffect, useMemo, useRef } from 'react'
import { parseCookies } from 'nookies'
import Head from 'next/head'
import Script from 'next/script'
import Router, { useRouter } from 'next/router'
import { DefaultSeo, NextSeo } from 'next-seo'
import minimatch from 'minimatch'
import { FlagsProvider } from 'flagged'
import { LOGO_THEME } from '@lib/constants/common'
import { staticFile, hasRoute } from '@common/utils'
import { SessionContext } from '@contexts/session'
import { userFetcher } from 'data-fetchers/common/user-fetcher'
import {
  UNAUTHORIZED_ONLY_ENDPOINTS,
  AUTHORIZED_ONLY_ENDPOINTS,
  UPDATE_INFO_HELLO_BAR_ENDPOINTS,
  LEADSQUARED_EXCLUSION_GLOBS,
  GENERAL_HELLO_BAR_EXCLUSION_GLOBS,
  RESTRICTED_PATHS,
  OPTIMONK_FORCE_INCLUDE_GLOBS,
  CLARITY_INCLUDE_GLOBS,
  FB_TEACHER_PIXEL_FORCE_INCLUDE_GLOBS,
  MIXPANEL_FUNNEL_GLOBS,
} from 'common/constants'
import { syncMixpanelUser } from '@lib/analytics/mixpanel'

import {
  generateUTMParametersDict,
  storeLastTouchUTMParametersInSessionCookie,
} from '@lib/analytics/track-utm'
import {
  LOCAL_STORAGE_UTM_PARAMS_KEY,
  SIGNUP_REFERRER_LOCAL_STORAGE_KEY,
} from '@lib/constants/tracking'
import { setToLocalStorage } from '@lib/utils/local-storage'

import {
  getFeaturesForUser,
  isTrustpilotRatingEnabled,
} from '@lib/utils/feature-flag'
import {
  AW_CONVERSION_ID,
  GA_TRACKING_ID,
  pageview,
  setGA4UserId,
  setGA4UserProperties,
} from '@lib/analytics/gtag'
import dynamic from 'next/dynamic'
import {
  redirectToForGetInitialProps,
  removeLocaleFromPathname,
} from '@lib/utils/common'
import { i18n } from '@lingui/core'
import {
  getLangLocaleFromLocale,
  handleLocaleRedirections,
  initTranslation,
  loadTranslation,
} from '@lib/utils/i18n'
import { I18nProvider } from '@lingui/react'
import { LOCALE_WISE_CANONICAL_LOCALIZED_PATH_GLOBS } from '@lib/constants/i18n'
import { defaultSeo, NOINDEX_PAGE_GLOBS } from '@lib/lp-seo'
import RoutingSpinner from '@components/common/routing-spinner'
import VerifyLocale from '@components/common/i18n/verify-locale'
import CompetitionHelloBar from '@components/hello-bar/competition-hello-bar'
import PasswordResetHelloBar from '@components/hello-bar/password-reset'
import useDetectCountry from '@hooks/useDetectCountry'
import { buildMetaUserData } from '@lib/utils/meta-user-data'
import AtlasWidget from '@components/common/atlas-widget'
import cogoToast from 'cogo-toast'
import { X } from 'react-feather'

const UpdateInfoHelloBar = dynamic(() =>
  import('@components/hello-bar/update-info-hello-bar')
)
const PUBLIC_SENTRY_DSN = process.env.PUBLIC_SENTRY_DSN
const isProd = process.env.NODE_ENV === 'production'

if (PUBLIC_SENTRY_DSN) {
  import('@sentry/browser').then((Sentry) =>
    Sentry.init({
      enabled: isProd,
      dsn: PUBLIC_SENTRY_DSN,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'TypeError: Failed to fetch',
        'TypeError: e.getAttribute is not a function',
        'TypeError: NetworkError when attempting to fetch resource.',
        'Non-Error exception captured',
        'Non-Error promise rejection captured',
        'ReferenceError: privateSpecialRepair is not defined',
        'Error: Cannot set headers after they are sent to the client',
        // 403 on classroom end-class PATCH — expected when a mod-login user
        // (not the assigned teacher) ends a class; the IsOwner permission denies
        // and the modal surfaces a toast. Not a code bug, drop from Sentry.
        /detail: You do not have permission to perform this action\..*URL: \/api\/v1\/classrooms\/\d+\//,
        // Errors caused by highlight.run
        'SecurityError: Blocked a frame with origin "https://www.codingal.com" from accessing a cross-origin frame.',
        "TypeError: Cannot read properties of undefined (reading 'prototype')",
        'TypeError: t.report is not a function',
        // Transient Firestore connectivity blip: a one-time read (.get) or a
        // listener's initial fetch fails because the client momentarily lost
        // network and had no cached copy. Already handled by try/catch in the
        // classroom presence hooks; not actionable, so drop from Sentry.
        'FirebaseError: Failed to get document because the client is offline.',
        'Failed to get document because the client is offline.',
      ],
      denyUrls: [
        // OptiMonk third-party widget
        /optimonk\.com/,
      ],
      beforeSend(event) {
        // Drop errors thrown inside the Facebook in-app browser's contact
        // autofill bridge. Generic "Cannot read properties of undefined
        // (reading 'value')" message means we can't filter via ignoreErrors
        // without risking real bugs, so match the bridge frame instead.
        const frames = event.exception?.values?.[0]?.stacktrace?.frames
        if (
          frames?.some(
            (f) => f.function === 'Object.setContactAutofillValuesFromBridge'
          )
        ) {
          return null
        }
        return event
      },
    })
  )
}

initTranslation(i18n)

declare global {
  interface Window {
    Offline: any
  }
  function pidTracker(id: string): void
}

const NON_CRITICAL_CSS_HREFS = [
  staticFile('/offline/offline-theme.css'),
  staticFile('/offline/offline-language.css'),
]

type Props = {
  Component: any
  pageProps: any
  err: any
  userData: any
  pathname: string
  modLoginUserId?: string
  modLoginUserName?: string
}

export default function App({
  Component,
  pageProps,
  err,
  userData,
  pathname,
  modLoginUserId,
  modLoginUserName,
}: Props) {
  const { id, name, email, loggedIn, teacher, phone, city } = userData
  const router = useRouter()
  const locale = router.locale || router.defaultLocale
  const firstRender = useRef(true)
  const { countryCode } = useDetectCountry({
    isLowerCase: true,
  })
  const isRestrictedPath = Boolean(hasRoute(pathname, RESTRICTED_PATHS))
  const shouldForceOptimonkScript = Boolean(
    hasRoute(pathname, OPTIMONK_FORCE_INCLUDE_GLOBS)
  )
  const shouldRenderOptimonkScript =
    shouldForceOptimonkScript || (!loggedIn && !isRestrictedPath)
  const shouldRenderClarityScript = Boolean(
    hasRoute(pathname, CLARITY_INCLUDE_GLOBS)
  )
  const isLoggedInStudent = loggedIn && !teacher
  const shouldUseTeacherFBPixel =
    !isLoggedInStudent &&
    ((loggedIn && teacher) ||
      hasRoute(pathname, FB_TEACHER_PIXEL_FORCE_INCLUDE_GLOBS))
  // Meta Pixel (dataset) IDs. Teachers get their own dataset so teacher-funnel
  // events are attributed separately from the student dataset.
  const FB_PIXEL_ID_STUDENT = '443675013217398' // student dataset
  const FB_PIXEL_ID_TEACHER = '2398417997352384' // teacher dataset
  const fbPixelId = shouldUseTeacherFBPixel
    ? FB_PIXEL_ID_TEACHER
    : FB_PIXEL_ID_STUDENT

  useEffect(() => {
    if (isProd && loggedIn && PUBLIC_SENTRY_DSN) {
      import('@sentry/node')
        .then((Sentry) => {
          Sentry.setUser({ email: email })
        })
        .catch((error) => {
          console.error('Failed to set Sentry user:', error)
        })
    }
  }, [email, isProd, loggedIn])

  if (pageProps.translation && firstRender.current) {
    const langLocale = getLangLocaleFromLocale(locale)
    // Load the translations for the locale
    i18n.load(langLocale, pageProps.translation)
    i18n.activate(langLocale)
    // Render only once
    firstRender.current = false
  }

  // listen for the locale changes
  useEffect(() => {
    if (pageProps.translation) {
      const langLocale = getLangLocaleFromLocale(locale)
      i18n.load(langLocale, pageProps.translation)
      i18n.activate(langLocale)
    }
  }, [locale, pageProps.translation])

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (isProd) {
        pageview(url)
      }
    }

    Router.events.on('routeChangeComplete', handleRouteChange)

    return () => Router.events.off('routeChangeComplete', handleRouteChange)
  }, [])

  // Set GA4 user_id and user_properties for logged-in users
  useEffect(() => {
    if (!isProd) return

    if (loggedIn && id) {
      // Set user_id
      setGA4UserId(id)

      // Determine user type
      const userType = teacher ? 'teacher' : 'student'

      // Set user properties
      setGA4UserProperties({
        // Core properties
        user_type: userType,
        grade: userData?.student?.grade,
        country: userData?.country || countryCode,
        city: userData?.city,
        timezone: userData?.timezone,
        gender: userData?.gender,

        // User status
        is_paid_user: userData?.isPaidUser,
        is_vip: userData?.student?.isVip,
        is_competition_user: userData?.isCompetitionUser,
        has_laptop: userData?.hasLaptop,

        // Acquisition
        signup_source: userData?.signupSource,
        utm_source: userData?.leadSource?.utmSource,
        utm_medium: userData?.leadSource?.utmMedium,
        utm_campaign: userData?.leadSource?.utmCampaign,

        // Student-specific
        enrolled_subjects: userData?.student?.enrolledSubjects?.join(', '),
        has_completed_course_class: userData?.student?.hasCompletedCourseClass,
        is_trial_class_scheduled: userData?.student?.isTrialClassScheduled,
        has_coding_teacher: !!userData?.student?.codingClassTeacherDetails,
        has_math_teacher: !!userData?.student?.mathClassTeacherDetails,
        has_ela_teacher: !!userData?.student?.elaClassTeacherDetails,

        // Teacher-specific
        is_verified_teacher: userData?.teacher?.isVerified,
        teacher_has_completed_class: userData?.teacher?.hasCompletedClass,
        educational_qualification: userData?.teacher?.educationalQualification,
      })
    } else {
      // Guest user - only set user type
      setGA4UserProperties({
        user_type: 'guest',
      })
    }
  }, [loggedIn, id, userData, teacher, countryCode])

  // Listen for moderator login events from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'mod-login-reload' && e.newValue === 'true') {
        // Clear the flag to prevent multiple navigations
        localStorage.removeItem('mod-login-reload')
        // Navigate to dashboard
        window.location.href = '/dashboard/'
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Show toast when user is in moderator login mode
  useEffect(() => {
    if (
      loggedIn &&
      !teacher &&
      modLoginUserId &&
      modLoginUserId !== id.toString()
    ) {
      const timer = setTimeout(() => {
        const { hide } = cogoToast.info(
          <div className="flex items-center space-x-2">
            <p>
              You are viewing this student&apos;s dashboard via mod-login. Some
              features may be unavailable.
            </p>
            <X className="w-1 text-grey" />
          </div>,
          { hideAfter: 500, onClick: () => hide() }
        )
      }, 1000) // Delay to ensure page is loaded

      return () => clearTimeout(timer)
    }
  }, [modLoginUserId, modLoginUserName, name, loggedIn])

  useEffect(() => {
    if (!loggedIn || !isProd) return

    // Identifying a user when logged in
    window?.globalThis?.analytics?.identify(id, {
      name: name,
      email: email,
    })
  }, [loggedIn])

  useEffect(() => {
    window?.globalThis?.analytics?.page()
  }, [pathname])

  // Re-identify logged-in students on funnel pages so events after a login or
  // a return visit join the right user, and refresh their attributes (grade,
  // country, zone, ...) so every event carries the current values. Runs on
  // each funnel page load; registering is local, so it costs no request.
  // Mod-login sessions are teachers acting as a student, so they stay out.
  useEffect(() => {
    if (!loggedIn || teacher || modLoginUserId) return
    if (!hasRoute(pathname, MIXPANEL_FUNNEL_GLOBS)) return
    syncMixpanelUser(userData)
  }, [loggedIn, id, pathname, userData])

  useEffect(() => {
    const utmParametersDict = generateUTMParametersDict(router)
    if (utmParametersDict)
      setToLocalStorage(
        LOCAL_STORAGE_UTM_PARAMS_KEY,
        JSON.stringify(utmParametersDict),
        () => storeLastTouchUTMParametersInSessionCookie(router)
      )
  }, [])

  useEffect(() => {
    const referrer = document.referrer
    !referrer.includes(window.location.hostname) &&
      setToLocalStorage(SIGNUP_REFERRER_LOCAL_STORAGE_KEY, referrer, () =>
        console.error(
          `Failed to save in local storage. Key: ${SIGNUP_REFERRER_LOCAL_STORAGE_KEY}`
        )
      )
  }, [])

  const pathnameWithoutQuery = pathname.split('?')?.[0] // Remove query parameters from pathname

  const canonicalLink = useMemo(() => {
    const canonicalBase = `https://www.codingal.com${pathnameWithoutQuery}`
    const localizedCanonicalGlobs =
      LOCALE_WISE_CANONICAL_LOCALIZED_PATH_GLOBS?.[locale] ?? []
    const shouldUseLocalizedCanonical =
      locale !== router.defaultLocale &&
      localizedCanonicalGlobs.some((glob) =>
        minimatch(pathnameWithoutQuery, glob)
      )

    return shouldUseLocalizedCanonical
      ? `https://www.codingal.com/${locale}${pathnameWithoutQuery}`
      : canonicalBase
  }, [locale, pathnameWithoutQuery, router.defaultLocale])

  const noindex = useMemo(
    () => NOINDEX_PAGE_GLOBS.some((glob) => minimatch(pathname, glob)),
    [pathname, locale]
  )

  const languageAlternates = useMemo(() => {
    // Define your supported locales here based on LOCALE_WISE_CANONICAL_LOCALIZED_PATH_GLOBS
    // router.defaultLocale ('en') is included explicitly since it has no key in that map
    const supportedLocales = [
      ...Object.keys(LOCALE_WISE_CANONICAL_LOCALIZED_PATH_GLOBS),
      router.defaultLocale,
    ]

    // Generate the hreflang URLs for all supported locales except the current locale
    const alternates = supportedLocales
      .filter((lang) => lang !== locale) // Skip the current locale
      .map((lang) => ({
        hrefLang: lang,
        href:
          lang === router.defaultLocale
            ? `https://www.codingal.com${pathnameWithoutQuery}` // Default locale without lang prefix
            : `https://www.codingal.com/${lang}${pathnameWithoutQuery}`, // Other locales with lang prefix
      }))

    // Add the current page version's hreflang (self-referencing)
    alternates.push({
      hrefLang: locale,
      href:
        locale === router.defaultLocale
          ? `https://www.codingal.com${pathnameWithoutQuery}` // Current page for default locale
          : `https://www.codingal.com/${locale}${pathnameWithoutQuery}`, // Current page for non-default locale
    })

    // Optionally include x-default for users without a language preference
    alternates.push({
      hrefLang: 'x-default',
      href: `https://www.codingal.com${pathnameWithoutQuery}`, // Default to base URL
    })

    return alternates
  }, [pathname, locale, router.defaultLocale])

  const shouldDisableHelloBars = useMemo(() => {
    return !GENERAL_HELLO_BAR_EXCLUSION_GLOBS.some((glob) =>
      minimatch(pathname, glob)
    )
  }, [pathname])

  return (
    // Workaround for https://github.com/vercel/next.js/issues/8592
    <>
      <DefaultSeo {...defaultSeo} />

      <Head>
        <link
          rel="icon"
          href={staticFile(
            `/images/logos/logos-${LOGO_THEME}/favicon-32x32.png`
          )}
          type="image/png"
          sizes="32x32"
        />
        <link
          rel="icon"
          href={staticFile(
            `/images/logos/logos-${LOGO_THEME}/favicon-90x90.png`
          )}
          type="image/png"
          sizes="90x90"
        />

        {/* Making non-critical css non-render-blocking.
            More info: https://web.dev/defer-non-critical-css/#optimize
        */}
        {NON_CRITICAL_CSS_HREFS.map((cssHref) => (
          <Fragment key={cssHref}>
            <link
              rel="preload"
              href={cssHref}
              as="style"
              // eslint-disable-next-line
              // @ts-ignore
              onLoad="this.onload=null;this.rel='stylesheet'"
            />
            <noscript>
              <link rel="stylesheet" href={cssHref} />
            </noscript>
          </Fragment>
        ))}
        <meta name="theme-color" content="#e9543e" />
        {
          // Temp fix for classroom recording. This key will be valid until September 4, 2024,
          loggedIn && (
            <meta
              httpEquiv="origin-trial"
              content="ApjlVMPageBpAGC9y5rV1LTReG4OGB0UenV8x2WNWCwW62c0mSeU9NwOe4DG4e/aYR06dnmogn4Ma9it28NDAQEAAAB9eyJvcmlnaW4iOiJodHRwczovL2NvZGluZ2FsLmNvbTo0NDMiLCJmZWF0dXJlIjoiRGlzYWJsZVRoaXJkUGFydHlTdG9yYWdlUGFydGl0aW9uaW5nIiwiZXhwaXJ5IjoxNzI1NDA3OTk5LCJpc1N1YmRvbWFpbiI6dHJ1ZX0="
            />
          )
        }
      </Head>

      {/* Scripts */}
      <>
        <Script
          strategy="lazyOnload"
          type="text/javascript"
          src={staticFile('/offline/offline.js')}
          onLoad={() => {
            {
              /* The script by default requests favicon.ico to check whether the user is online or not.
     We do not have a file with that name so we will be checking for favicon-32x32.png
        */
            }
            if (window?.Offline)
              window.Offline.options = {
                checks: {
                  xhr: {
                    url: staticFile(
                      `/images/logos/logos-${LOGO_THEME}/favicon-32x32.png`
                    ),
                  },
                },
              }
          }}
        />
        {locale === 'translate' && (
          <>
            <Script
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: `
              var _jipt = []; _jipt.push(['project', 'web-i18n']);
            `,
              }}
            />
            <Script
              type="text/javascript"
              src="//cdn.crowdin.com/jipt/jipt.js"
            />
          </>
        )}

        {isProd && (
          <>
            {/* Google Ads - Global site tag */}
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
            ></script>
            <Script
              dangerouslySetInnerHTML={{
                __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());

                        // Google Analytics
                        gtag('config', '${GA_TRACKING_ID}', { 'page_path': window.location.pathname, 'allow_enhanced_conversions': true });

                        // Google Ads
                        gtag('config', '${AW_CONVERSION_ID}', { 'allow_enhanced_conversions': true });
                        `,
              }}
            />

            {/* Facebook */}
            <Script
              dangerouslySetInnerHTML={{
                __html: `
                        !function(f,b,e,v,n,t,s)
                        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                        n.queue=[];t=b.createElement(e);t.async=!0;
                        t.src=v;s=b.getElementsByTagName(e)[0];
                        s.parentNode.insertBefore(t,s)}(window, document,'script',
                        'https://connect.facebook.net/en_US/fbevents.js');
                      `,
              }}
            />
            {/*
              next/script dedupes injected scripts by `id` for the lifetime of
              the SPA session (see next/dist/client/script.js LoadCache). Most
              users first load the site logged out, which mounts the anon
              variant below. Keying both variants off the same id would let
              that anon init win the LoadCache race forever, so the later
              logged-in init (which carries external_id/em/ph/fn/ln/ct) would never
              actually run once the user signs in via client-side navigation.
              Keying the logged-in id by user id lets it fire once per user.
            */}
            {loggedIn && (
              <Script
                id={`fb-init-${id}`}
                dangerouslySetInnerHTML={{
                  __html: `
                          fbq('init', '${fbPixelId}', ${JSON.stringify(
                    buildMetaUserData(
                      {
                        id,
                        parentName: userData?.student?.parentName,
                        email,
                        phone,
                        city,
                        metaFbc: userData?.metaFbc,
                      },
                      countryCode,
                      parseCookies()
                    )
                  )});
                        `,
                }}
              />
            )}
            {!loggedIn && (
              <Script
                id="fb-init-anon"
                dangerouslySetInnerHTML={{
                  __html: `
                          fbq('init', '${fbPixelId}');
                        `,
                }}
              />
            )}

            {/* LeadSquared Tracking Code Start */}
            {!LEADSQUARED_EXCLUSION_GLOBS.some((glob) =>
              minimatch(pathname, glob)
            ) && (
              <>
                <Script
                  src="https://web-in21.mxradon.com/t/Tracker.js"
                  strategy="lazyOnload"
                  onLoad={() => {
                    pidTracker('51399')
                  }}
                />
              </>
            )}
            {/*LeadSquared Tracking Code End*/}

            {/* Rewardfull */}
            <Script
              id="rw-init"
              dangerouslySetInnerHTML={{
                __html: `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');

                (function() {
                  var el = document.createElement('script');
                  el.setAttribute('src', 'https://r.wdfl.co/rw.js');
                  el.setAttribute('data-rewardful', '627bb0');
                  document.body.appendChild(el);
                })();`,
              }}
            />

            {/* Optimonk Script */}
            {shouldRenderOptimonkScript && (
              <Script
                src="https://onsite.optimonk.com/script.js?account=259580"
                strategy="lazyOnload"
              />
            )}

            {/* Microsoft Clarity - on trial-class, login, register pages */}
            {shouldRenderClarityScript && (
              <Script
                id="ms-clarity"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{
                  __html: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "w1a1yfy5tt");`,
                }}
              />
            )}
          </>
        )}
        {isTrustpilotRatingEnabled() && (
          <script
            type="text/javascript"
            src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
            async
          ></script>
        )}
      </>
      <I18nProvider i18n={i18n}>
        <SessionContext.Provider
          value={{ ...userData, modLoginUserId, modLoginUserName }}
        >
          <FlagsProvider features={getFeaturesForUser(userData)}>
            <AtlasWidget />
            {shouldDisableHelloBars && (
              <>
                <PasswordResetHelloBar />
              </>
            )}
            <CompetitionHelloBar />
            {userData.loggedIn && !teacher && (
              <>
                <UpdateInfoHelloBar
                  allowedPaths={UPDATE_INFO_HELLO_BAR_ENDPOINTS}
                />
              </>
            )}
            <Component {...pageProps} err={err} />
            <VerifyLocale />
            <RoutingSpinner />
          </FlagsProvider>
        </SessionContext.Provider>
      </I18nProvider>

      <NextSeo
        noindex={noindex}
        nofollow={noindex}
        canonical={noindex ? undefined : `${canonicalLink}`}
        languageAlternates={noindex ? undefined : languageAlternates}
      />
    </>
  )
}

App.getInitialProps = async ({ Component, ctx }) => {
  const next = ctx.query?.next
  const isBuilding = process.env.APP_ENV === 'build'
  !isBuilding && (await handleLocaleRedirections(ctx))

  const locale = ctx.locale || ctx.defaultLocal
  const pathname = removeLocaleFromPathname(ctx.asPath, locale)

  let pageProps = {}
  const cookies = parseCookies(ctx)
  const token = cookies.token
  const modLoginUserId = cookies.mod_login_user_id
  const modLoginUserName = cookies.mod_login_user_name
  const userData = await userFetcher('/api/v1/users/me/', token, ctx)
  const queryString = ctx.asPath.split('?')[1]
  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx)
  }

  if (userData.loggedIn && hasRoute(pathname, UNAUTHORIZED_ONLY_ENDPOINTS)) {
    if (next) {
      const updatedQueryString = new URLSearchParams(queryString)
      updatedQueryString.delete('next')
      redirectToForGetInitialProps(ctx, `${next}?${updatedQueryString}`)
    } else if (queryString?.length > 0)
      redirectToForGetInitialProps(ctx, `/dashboard/?${queryString}`)
    else redirectToForGetInitialProps(ctx, '/dashboard')
  } else if (
    !userData.loggedIn &&
    hasRoute(pathname, AUTHORIZED_ONLY_ENDPOINTS)
  ) {
    const nextPath = pathname.split('?')[0]
    if (queryString?.length > 0)
      redirectToForGetInitialProps(
        ctx,
        `/login/?next=${nextPath}&${queryString}`
      )
    else redirectToForGetInitialProps(ctx, `/login/?next=${pathname}`)
  }

  const translation = await loadTranslation(locale)

  return {
    pageProps: { ...pageProps, translation },
    userData,
    pathname,
    modLoginUserId,
    modLoginUserName,
  }
}
